import type { ProductListItemModel } from 'autogen/swagger/all';
import type { MessageRequest } from 'autogen/swagger/comai';
import { closeAllDrawers, isDrawerOpen, openDrawers, useDrawerStore } from 'components/generic/Drawer/hooks/useDrawer';
import useComAIStore from '../store/comAIStore';
import { COM_AI_TERMS_DRAWER_ID } from './consts';
import type { AIChatResponse, SendMessageArgs, UserMessageContext } from './types';

export function processTextChunk(textChunk: string) {
    const addAIMessage = useComAIStore.getState().addAIMessage;
    const jsonArray = textChunk.split('data: ').filter(Boolean); // Split by 'data: ' and filter out empty strings
    const failedChunks: string[] = [];

    jsonArray.forEach((jsonText) => {
        const combinedJsonText = failedChunks.length ? failedChunks.pop() + jsonText : jsonText;
        let jsonChunk = {} as AIChatResponse;

        try {
            try {
                jsonChunk = JSON.parse(combinedJsonText); // Parse the string into JSON
            } catch {
                failedChunks.push(combinedJsonText);
                throw new Error('parse_error');
            }

            addAIMessage(jsonChunk);
        } catch (error) {
            if ((error as Error).message !== 'parse_error') {
                console.error('Error processing chunk:', error);
            }
        }
    });
}

const processStreamResponse = async (response: Response) => {
    if (!response.body) {
        console.error('ReadableStream not yet supported in this browser.');
        return;
    }

    // https://developer.mozilla.org/en-US/docs/Web/API/Streams_API/Using_readable_streams
    const decoder = new TextDecoder();
    const reader = response.body.getReader();
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const textChunk = decoder.decode(value);
        processTextChunk(textChunk);
    }
};

function extractProductFromContext(context: MessageRequest['context']): UserMessageContext['product'] | undefined {
    if (context?.product_id && context?.product_name) {
        return {
            id: context.product_id,
            name: context.product_name
        };
    }
}

export const sendMessage: SendMessageArgs = async (newMessage, context = {}) => {
    // Ensure terms have been accepted before proceeding. We only set loading state once allowed to send.
    const store = useComAIStore.getState();
    const { initialData, addUserMessage, setIsAIResponseLoading } = store;

    if (!store.hasAcceptedTerms) {
        // Open the terms drawer (await its open animation) if not already open and then wait for acceptance.
        const isOpen = isDrawerOpen(COM_AI_TERMS_DRAWER_ID);
        if (!isOpen) {
            await openDrawers([{ id: COM_AI_TERMS_DRAWER_ID, props: { disableOverlay: true } }]);
        }
        try {
            await waitForAcceptedTerms();
        } catch {
            // User dismissed the terms drawer without acceptance; abort send.
            closeAllDrawers();
            return;
        }
    }

    setIsAIResponseLoading(true);

    if (!initialData) {
        return;
    }

    if (!context?.hideMessage) {
        addUserMessage(newMessage, { ...context, product: extractProductFromContext(context) });
    }

    const body: MessageRequest = {
        new_message: newMessage,
        context: {
            product_id: context.product_id,
            product_name: context.product_name
        }
    };

    try {
        const response = await fetch(initialData.chatUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        try {
            await processStreamResponse(response);
        } catch {
            throw Error('Error in processStreamResponse');
        }
    } finally {
        useComAIStore.getState().setIsAIResponseLoading(false);
    }
};

export const getProductName = (listName: ProductListItemModel['listName']) => {
    const name = listName?.lines?.map((line) => line?.value ?? '').join(' ');
    if (name) {
        return name;
    }
};

// Promise helper that resolves when terms are accepted or rejects if the terms drawer closes without acceptance.
let acceptanceWaitPromise: Promise<void> | null = null;
async function waitForAcceptedTerms() {
    if (useComAIStore.getState().hasAcceptedTerms) return; // Already accepted.
    if (acceptanceWaitPromise) return acceptanceWaitPromise; // Reuse ongoing wait.

    acceptanceWaitPromise = new Promise<void>((resolve, reject) => {
        const unsubscribeHasAccepted = useComAIStore.subscribe((state) => {
            if (state.hasAcceptedTerms) {
                cleanup();
                resolve();
            }
        });

        const unsubscribeDrawer = useDrawerStore.subscribe((state) => {
            const termsDrawerOpen = state.drawerInstances.some((d) => d.id === COM_AI_TERMS_DRAWER_ID);
            if (!termsDrawerOpen && !useComAIStore.getState().hasAcceptedTerms) {
                // Drawer closed without acceptance.
                cleanup();
                reject(new Error('terms_not_accepted'));
            }
        });

        function cleanup() {
            unsubscribeHasAccepted();
            unsubscribeDrawer();
            acceptanceWaitPromise = null;
        }
    });
    return acceptanceWaitPromise;
}
